import { useMemo } from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import { formatUTCDateTime } from '../../utils/timeUtils';
import LatencyStatsDisplay from './LatencyStatsDisplay';
import LatencyBadge from './LatencyBadge';

/**
 * Component to display details of messages in a selected time interval
 */
const MessageDetails = ({
  messages = [],
  sourceColors = {},
  sourceNames = {},
  timestamp,
  endTimestamp,
  isRange = false,
  onViewRecord
}) => {
  // Format timestamp for display - memoized to prevent recalculation
  const formatTimestamp = useMemo(() => {
    // Create a cache for formatted timestamps
    const cache = new Map();

    return (timestamp) => {
      if (!timestamp) return 'N/A';

      // Return cached value if available
      if (cache.has(timestamp)) {
        return cache.get(timestamp);
      }

      try {
        const formatted = formatUTCDateTime(timestamp);

        // Cache the result (limit cache size to prevent memory issues)
        if (cache.size > 100) {
          // Clear the first entry if cache gets too large
          const firstKey = cache.keys().next().value;
          cache.delete(firstKey);
        }
        cache.set(timestamp, formatted);

        return formatted;
      } catch (error) {
        return String(timestamp);
      }
    };
  }, []);

  // Group messages by source - memoized to prevent recalculation on every render
  const messagesBySource = useMemo(() => {
    return messages.reduce((acc, message) => {
      const sourceId = message.sourceId || 'unknown';
      if (!acc[sourceId]) {
        acc[sourceId] = [];
      }
      acc[sourceId].push(message);
      return acc;
    }, {});
  }, [messages]);

  // Sort sources by message count (descending) - memoized
  const sortedSources = useMemo(() => {
    return Object.keys(messagesBySource).sort(
      (a, b) => messagesBySource[b].length - messagesBySource[a].length
    );
  }, [messagesBySource]);

  // Compute percentile latency stats from the current message set
  const messageLatencyStats = useMemo(() => {
    const samples = messages
      .map(m => m.leader_latency_ms)
      .filter(v => typeof v === 'number' && isFinite(v))
      .sort((a, b) => a - b);
    if (samples.length === 0) return { p50: null, p95: null, p99: null };
    const pct = (p) => samples[Math.max(0, Math.ceil((p / 100) * samples.length) - 1)];
    return { p50: pct(50), p95: pct(95), p99: pct(99) };
  }, [messages]);

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">
          Message Details for Selected {isRange ? 'Range' : 'Time'}:
          {timestamp && (
            <span className="ms-2 text-muted fs-6">
              {isRange && endTimestamp
                ? `${formatTimestamp(timestamp)} - ${formatTimestamp(endTimestamp)}`
                : formatTimestamp(timestamp)}
            </span>
          )}
        </h5>
      </Card.Header>
      <Card.Body>
        {messages.length === 0 ? (
          <div className="text-center p-4">
            <p className="mb-0">No messages in this time interval</p>
            {timestamp && (
              <p className="text-muted mb-0">
                {isRange && endTimestamp
                  ? `${formatUTCDateTime(timestamp)} - ${formatUTCDateTime(endTimestamp)}`
                  : formatUTCDateTime(timestamp)}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">Summary ({messages.length.toLocaleString()})</h6>
                <LatencyStatsDisplay latencyStats={messageLatencyStats} />
              </div>
              <div className="d-flex flex-wrap gap-2">
                {sortedSources.map(sourceId => (
                  <Badge
                    key={sourceId}
                    bg="light"
                    text="dark"
                    className="p-2 border"
                    style={{
                      borderColor: sourceColors[sourceId] || '#6c757d',
                      borderLeftWidth: '4px'
                    }}
                  >
                    {sourceNames[sourceId] || sourceId}: {messagesBySource[sourceId].length.toLocaleString()}
                  </Badge>
                ))}
              </div>
            </div>

            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Name</th>
                  <th>Timestamp</th>
                  <th>Latency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message, index) => (
                  <tr key={`${message.leader_id}-${index}`}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="me-2"
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: sourceColors[message.sourceId] || '#6c757d'
                          }}
                        ></div>
                        {message.sourceName || sourceNames[message.sourceId] || message.sourceId}
                      </div>
                    </td>
                    <td>{message.leader_name || message.data?.event__uid || 'N/A'}</td>
                    <td>{formatTimestamp(message.timestamp)}</td>
                    <td>
                      {message.leader_latency_ms != null ? (
                        <LatencyBadge latencyMs={message.leader_latency_ms} />
                      ) : 'N/A'}
                    </td>
                    <td>
                      {onViewRecord && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onViewRecord(message)}
                        >
                          View Record
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default MessageDetails;
